import { useCallback, useEffect, useMemo, useState } from "react";
import { Package, Save, Plus, Trash2, Info, Wallet, TrendingUp, DollarSign, Clock, Percent, Wrench, Zap, ReceiptText, Trophy, Award, Medal, Crown } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList, ReferenceLine,
} from "recharts";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import StatCard from "@/components/StatCard";
import ProdutividadeCard from "@/components/ProdutividadeCard";
import OnboardingAppsCard from "@/components/OnboardingAppsCard";
import { getConfig, updateConfig } from "@/lib/api";
import { fmtBRL, fmtPct } from "@/lib/format";
import { DEFAULT_PLANS, DEFAULT_BONUS_PCT } from "@/lib/plans";

// Custo de IMPLANTAÇÃO (one-time): drop, conector, equip1, equip2, hora×homem
const IMPLANTACAO_FIELDS = [
  { key: "drop", label: "Drop", unitLabel: "R$/metro", qtyLabel: "metros" },
  { key: "conector", label: "Conector de fibra", unitLabel: "R$/unidade", qtyLabel: "unidades" },
  { key: "equipamento", label: "Equipamento 1", unitLabel: "R$/unidade", qtyLabel: "unidades" },
  { key: "equipamento2", label: "Equipamento 2", unitLabel: "R$/unidade", qtyLabel: "unidades" },
  { key: "hora_homem", label: "Hora × Homem", unitLabel: "R$/hora", qtyLabel: "horas" },
];
// Custo MENSAL fixo (recorrente): mega, energia, app, suporte + imposto (% sobre venda)
const MENSAL_UNIT_FIELDS = [
  { key: "mega", label: "Mega (banda)", unitLabel: "R$/mega", qtyLabel: "megas" },
  { key: "energia", label: "Energia consumida", unitLabel: "R$/kWh", qtyLabel: "kWh" },
  { key: "app", label: "APP (licenças)", unitLabel: "R$/licença", qtyLabel: "licenças" },
  { key: "suporte", label: "Suporte técnico", unitLabel: "R$/atendimento", qtyLabel: "atendimentos" },
];

const num = (v) => Number(v || 0);
const totalOf = (p, k) => num(p[`custo_${k}_unit`]) * num(p[`custo_${k}_qty`]);
const totalImplantacao = (p) => IMPLANTACAO_FIELDS.reduce((s, f) => s + totalOf(p, f.key), 0);
const impostoValor = (p) => (num(p.price) * num(p.custo_imposto_pct)) / 100;
const totalMensal = (p) =>
  MENSAL_UNIT_FIELDS.reduce((s, f) => s + totalOf(p, f.key), 0) + impostoValor(p);
const lucroMensal = (p) => num(p.price) - totalMensal(p);
const margemLiquidaPct = (p) => (num(p.price) > 0 ? (lucroMensal(p) / num(p.price)) * 100 : 0);
const paybackMeses = (p) => {
  const impl = totalImplantacao(p);
  const lm = lucroMensal(p);
  if (lm <= 0) return null;
  if (impl <= 0) return 0;
  return impl / lm;
};
// Lucro contabilizando 12 meses mínimo do cliente:
// = price*12 − total_implantacao − total_mensal*12
const lucro12m = (p) => num(p.price) * 12 - totalImplantacao(p) - totalMensal(p) * 12;

const emptyPlan = () => ({
  name: "Novo plano", price: 0,
  custo_drop_unit: 0, custo_drop_qty: 0,
  custo_conector_unit: 0, custo_conector_qty: 0,
  custo_equipamento_unit: 0, custo_equipamento_qty: 0,
  custo_equipamento2_unit: 0, custo_equipamento2_qty: 0,
  custo_hora_homem_unit: 0, custo_hora_homem_qty: 0,
  custo_mega_unit: 0, custo_mega_qty: 0,
  custo_energia_unit: 0, custo_energia_qty: 0,
  custo_app_unit: 0, custo_app_qty: 0,
  custo_suporte_unit: 0, custo_suporte_qty: 0,
  custo_imposto_pct: 0,
});

const normalize = (p) => ({ ...emptyPlan(), ...p, name: p?.name || "" });

export default function PlanosTab() {
  const [plans, setPlans] = useState([]);
  const [bonus, setBonus] = useState(DEFAULT_BONUS_PCT);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cfg = await getConfig();
      const list = (Array.isArray(cfg?.plans) && cfg.plans.length) ? cfg.plans : DEFAULT_PLANS;
      setPlans(list.map(normalize));
      setBonus(typeof cfg.bonus_pct === "number" ? cfg.bonus_pct : DEFAULT_BONUS_PCT);
    } catch (err) {
      console.error("Erro ao carregar planos:", err);
      toast.error("Erro ao carregar planos");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setPlanField = (idx, field, val) => {
    setPlans((prev) => prev.map((p, i) => (i === idx ? { ...p, [field]: val } : p)));
  };
  const addPlan = () => setPlans((p) => [...p, emptyPlan()]);
  const removePlan = (idx) => {
    if (!window.confirm("Remover este plano?")) return;
    setPlans((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    const clean = plans
      .map((p) => ({
        ...normalize(p),
        price: num(p.price),
        custo_imposto_pct: num(p.custo_imposto_pct),
        commission: 0,
      }))
      .filter((p) => (p.name || "").trim().length > 0);
    if (clean.length === 0) { toast.error("Adicione ao menos um plano"); return; }
    setSaving(true);
    try {
      const saved = await updateConfig({ plans: clean, bonus_pct: num(bonus) });
      toast.success("Planos salvos");
      setPlans((prev) => (saved.plans || clean).map((p, i) => ({ ...prev[i], ...p })));
    } catch (err) {
      console.error("Erro ao salvar planos:", err);
      toast.error("Erro ao salvar planos");
    } finally { setSaving(false); }
  };

  // Dados para o gráfico de margem líquida vs preço
  const chartData = useMemo(() => plans.map((p) => ({
    name: p.name || "—",
    price: num(p.price),
    margem: Number(margemLiquidaPct(p).toFixed(1)),
    lucroMensal: Number(lucroMensal(p).toFixed(2)),
  })), [plans]);

  const totals = useMemo(() => {
    const tImpl = plans.reduce((s, p) => s + totalImplantacao(p), 0);
    const tMens = plans.reduce((s, p) => s + totalMensal(p), 0);
    const tPrice = plans.reduce((s, p) => s + num(p.price), 0);
    const tLucroMensal = tPrice - tMens;
    const mediaMargem = plans.length ? plans.reduce((s, p) => s + margemLiquidaPct(p), 0) / plans.length : 0;
    const paybacks = plans.map(paybackMeses).filter((v) => v !== null);
    const mediaPB = paybacks.length ? paybacks.reduce((s, v) => s + v, 0) / paybacks.length : null;
    return { tImpl, tMens, tPrice, tLucroMensal, mediaMargem, mediaPB };
  }, [plans]);

  const margemBarColor = (m) => (m >= 45 ? "#10b981" : m >= 25 ? "#f59e0b" : "#ef4444");

  // Ranking dos planos por margem líquida — usado na análise comparativa no topo.
  const planosRanked = useMemo(() => {
    return plans
      .map((p, idx) => ({
        idx,
        name: p.name || "(sem nome)",
        price: num(p.price),
        margem: margemLiquidaPct(p),
        lucroMensal: lucroMensal(p),
        totalImplantacao: totalImplantacao(p),
        totalMensal: totalMensal(p),
        payback: paybackMeses(p),
        lucro12m: lucro12m(p),
      }))
      .filter((p) => p.price > 0)
      .sort((a, b) => b.margem - a.margem);
  }, [plans]);

  const bestMargin = planosRanked[0]?.margem ?? 0;

  return (
    <div className="space-y-6" data-testid="planos-tab">
      {/* Análise comparativa por plano — visão individual + destaque do melhor */}
      {planosRanked.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-5" data-testid="analise-individual-planos">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <div className="flex items-center gap-2">
                <Trophy className="w-4 h-4 text-amber-500" />
                <h3 className="font-heading font-semibold text-lg">Análise por plano — qual entrega a melhor margem</h3>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Ranking por margem líquida (lucro mensal ÷ preço). O primeiro é o plano mais rentável por real vendido.
              </p>
            </div>
            <Badge variant="outline" className="bg-emerald-500/10 text-emerald-500 border-emerald-500/40 text-[10px]">
              Melhor: {planosRanked[0].name} · {fmtPct(bestMargin)}
            </Badge>
          </div>

          {/* Grid de mini-cards individuais por plano */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mb-5">
            {planosRanked.map((p, rank) => {
              const isWinner = rank === 0;
              const RankIcon = rank === 0 ? Crown : rank === 1 ? Award : rank === 2 ? Medal : Package;
              const rankColor = rank === 0 ? "text-amber-500" : rank === 1 ? "text-slate-300" : rank === 2 ? "text-orange-400" : "text-muted-foreground";
              const mrgTone = p.margem >= 45 ? "text-emerald-500" : p.margem >= 25 ? "text-amber-500" : "text-red-500";
              return (
                <div
                  key={p.idx}
                  className={`rounded-lg p-3 space-y-2 transition-all ${
                    isWinner
                      ? "border-2 border-emerald-500/50 bg-emerald-500/5 shadow-lg shadow-emerald-500/10"
                      : "border border-border bg-background"
                  }`}
                  data-testid={`plano-mini-${p.idx}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <RankIcon className={`w-4 h-4 shrink-0 ${rankColor}`} />
                      <span className="font-heading font-semibold truncate">{p.name}</span>
                    </div>
                    <Badge variant="outline" className={`text-[10px] tabular-nums shrink-0 ${isWinner ? "border-emerald-500/60 text-emerald-500" : ""}`}>
                      #{rank + 1}
                    </Badge>
                  </div>

                  <div className="flex items-baseline gap-1.5">
                    <span className={`text-2xl font-heading font-extrabold tabular-nums ${mrgTone}`} data-testid={`plano-mini-margem-${p.idx}`}>
                      {fmtPct(p.margem)}
                    </span>
                    <span className="text-[10px] text-muted-foreground uppercase tracking-widest">margem líquida</span>
                  </div>

                  {/* Barra proporcional à melhor margem */}
                  <div className="relative h-1.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className={`absolute inset-y-0 left-0 rounded-full ${isWinner ? "bg-emerald-500" : "bg-primary/60"}`}
                      style={{ width: `${Math.max(0, Math.min(100, bestMargin > 0 ? (p.margem / bestMargin) * 100 : 0))}%` }}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-[10px] pt-1">
                    <div>
                      <div className="text-muted-foreground uppercase tracking-widest">Preço</div>
                      <div className="font-mono tabular-nums text-emerald-500">{fmtBRL(p.price)}<span className="text-muted-foreground font-normal">/mês</span></div>
                    </div>
                    <div className="text-right">
                      <div className="text-muted-foreground uppercase tracking-widest">Lucro mensal</div>
                      <div className={`font-mono tabular-nums ${p.lucroMensal >= 0 ? "text-primary" : "text-red-500"}`}>{fmtBRL(p.lucroMensal)}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground uppercase tracking-widest">Implantação</div>
                      <div className="font-mono tabular-nums text-amber-500">{fmtBRL(p.totalImplantacao)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-muted-foreground uppercase tracking-widest">Payback</div>
                      <div className="font-mono tabular-nums text-foreground">
                        {p.payback === null ? "—" : p.payback === 0 ? "imediato" : `${p.payback.toFixed(1)}m`}
                      </div>
                    </div>
                  </div>

                  {isWinner && (
                    <div className="flex items-center gap-1.5 text-[10px] text-emerald-500 pt-1 border-t border-emerald-500/20">
                      <Trophy className="w-3 h-3" />
                      <span className="font-semibold">Melhor rentabilidade por real vendido</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Gráfico horizontal comparativo */}
          <div className="rounded-lg border border-border bg-background p-3" data-testid="chart-ranking-margem">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
              <TrendingUp className="w-3 h-3" /> Comparativo direto — margem por plano
            </div>
            <div className="h-[max(180px,calc(60px*var(--n)))]" style={{ ["--n"]: planosRanked.length }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={planosRanked} layout="vertical" margin={{ top: 4, right: 60, left: 4, bottom: 4 }}>
                  <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(v) => `${v.toFixed(0)}%`} domain={[0, 100]} />
                  <YAxis type="category" dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11} width={120} />
                  <ReferenceLine x={45} stroke="#10b981" strokeDasharray="3 3" />
                  <ReferenceLine x={25} stroke="#f59e0b" strokeDasharray="3 3" />
                  <Tooltip
                    contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                    formatter={(v) => [`${v.toFixed(1)}%`, "Margem líquida"]}
                    labelFormatter={(label, payload) => {
                      const row = payload?.[0]?.payload;
                      return row ? `${label} · ${fmtBRL(row.price)}/mês · lucro ${fmtBRL(row.lucroMensal)}/mês` : label;
                    }}
                  />
                  <Bar dataKey="margem" radius={[0, 6, 6, 0]}>
                    {planosRanked.map((p, i) => (
                      <Cell key={i} fill={i === 0 ? "#10b981" : margemBarColor(p.margem)} />
                    ))}
                    <LabelList dataKey="margem" position="right" formatter={(v) => `${v.toFixed(1)}%`} fontSize={11} fill="hsl(var(--foreground))" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* KPIs top */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard testId="planos-kpi-receita-mensal" label="Receita mensal (soma)"
          value={fmtBRL(totals.tPrice)} hint={`${plans.length} plano(s) ativo(s)`} icon={DollarSign} />
        <StatCard testId="planos-kpi-implantacao" label="Custo de implantação (soma)"
          value={fmtBRL(totals.tImpl)} hint="Investimento one-time por plano" icon={Wrench} tone="warning" />
        <StatCard testId="planos-kpi-mensal" label="Custo mensal fixo (soma)"
          value={fmtBRL(totals.tMens)} hint="Recorrente: mega + energia + imposto" icon={Zap} tone="warning" />
        <StatCard testId="planos-kpi-lucro-mensal" label="Lucro mensal líquido"
          value={fmtBRL(totals.tLucroMensal)} hint="Receita − custos mensais (sem implantação)"
          icon={TrendingUp} tone={totals.tLucroMensal > 0 ? "success" : "danger"} />
        <StatCard testId="planos-kpi-payback-medio" label="Payback médio"
          value={totals.mediaPB === null ? "—" : `${totals.mediaPB.toFixed(1)} mês${totals.mediaPB >= 2 ? "es" : ""}`}
          hint={`Margem média ${fmtPct(totals.mediaMargem)}`} icon={Clock}
          tone={totals.mediaPB === null ? "danger" : totals.mediaPB <= 6 ? "success" : totals.mediaPB <= 12 ? "warning" : "danger"} />
      </div>

      {/* Toolbar */}
      <div className="rounded-xl border border-border bg-card p-4 flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm">
          <Info className="w-4 h-4 text-primary" />
          <span className="text-muted-foreground">
            Cada plano tem <strong className="text-foreground">implantação</strong> (one-time) e <strong className="text-foreground">custo mensal</strong>. Payback = implantação ÷ lucro mensal · Margem = lucro mensal ÷ preço.
          </span>
        </div>
        <div className="flex items-center gap-2 sm:ml-auto flex-wrap">
          <Label className="text-xs text-muted-foreground shrink-0">Bônus meta (%)</Label>
          <Input type="number" step="0.01" min="0" max="1" value={bonus}
            onChange={(e) => setBonus(e.target.value)} data-testid="input-bonus-pct" className="w-24" />
          <span className="text-xs text-muted-foreground tabular-nums">{fmtPct((num(bonus)) * 100)}</span>
          <Button variant="outline" size="sm" onClick={addPlan} data-testid="btn-add-plan">
            <Plus className="w-4 h-4 mr-1.5" /> Plano
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid="btn-save-planos">
            <Save className="w-4 h-4 mr-1.5" /> Salvar tudo
          </Button>
        </div>
      </div>

      {loading && <div className="text-center text-muted-foreground py-10">Carregando planos…</div>}
      {!loading && plans.length === 0 && (
        <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground">
          Nenhum plano cadastrado. Clique em <strong>+ Plano</strong> para começar.
        </div>
      )}

      {/* Gráfico: Margem líquida % por plano vs Preço */}
      {!loading && plans.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-5" data-testid="chart-margem-liquida">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-primary" />
            <h3 className="font-heading font-semibold text-lg">Margem líquida × Preço de venda</h3>
            <Badge variant="outline" className="ml-auto">sem custo de implantação</Badge>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            % do valor de venda mensal que vira lucro líquido após os custos recorrentes (mega, energia, imposto).
            Ignora a implantação — mostra o que realmente sobra por cliente ativo/mês.
          </p>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 20, right: 24, left: 0, bottom: 8 }}>
                <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
                <ReferenceLine y={45} stroke="#10b981" strokeDasharray="4 4" label={{ value: "45% (ótimo)", position: "right", fontSize: 10, fill: "#10b981" }} />
                <ReferenceLine y={25} stroke="#f59e0b" strokeDasharray="4 4" label={{ value: "25% (atenção)", position: "right", fontSize: 10, fill: "#f59e0b" }} />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                  formatter={(v, name, item) => {
                    if (name === "Margem líquida %") return [`${v}%`, name];
                    return [v, name];
                  }}
                  labelFormatter={(label, payload) => {
                    const row = payload?.[0]?.payload;
                    if (!row) return label;
                    return `${label} · ${fmtBRL(row.price)}/mês · lucro ${fmtBRL(row.lucroMensal)}/mês`;
                  }}
                />
                <Bar dataKey="margem" name="Margem líquida %" radius={[6, 6, 0, 0]}>
                  {chartData.map((d, i) => (
                    <Cell key={i} fill={margemBarColor(d.margem)} />
                  ))}
                  <LabelList dataKey="margem" position="top" formatter={(v) => `${v}%`} fontSize={11} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {!loading && plans.length > 0 && <ProdutividadeCard plans={plans} />}

      {/* Apps customizáveis do formulário de onboarding (admin) */}
      <OnboardingAppsCard />

      {/* Cards de plano */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {plans.map((p, idx) => {
          const impl = totalImplantacao(p);
          const mens = totalMensal(p);
          const lm = lucroMensal(p);
          const marg = margemLiquidaPct(p);
          const pb = paybackMeses(p);
          const l12 = lucro12m(p);
          const mrgTone = marg >= 45 ? "text-emerald-500" : marg >= 25 ? "text-amber-500" : "text-red-500";
          return (
            <div key={p.name + idx} className="rounded-xl border border-border bg-card p-5 space-y-4" data-testid={`plano-card-${idx}`}>
              {/* Cabeçalho */}
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0 space-y-1.5">
                  <Label className="text-xs">Nome do plano</Label>
                  <Input value={p.name} onChange={(e) => setPlanField(idx, "name", e.target.value)}
                    placeholder="Ex: Premium" data-testid={`input-plan-name-${idx}`}
                    className="text-base font-heading font-semibold" />
                </div>
                <Button variant="ghost" size="icon" onClick={() => removePlan(idx)}
                  data-testid={`btn-remove-plan-${idx}`} className="mt-6">
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>

              {/* Preço */}
              <div className="rounded-lg border border-border bg-background p-3 space-y-1.5">
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Valor de venda (mensal)</Label>
                <Input type="number" step="0.01" min="0" value={p.price}
                  onChange={(e) => setPlanField(idx, "price", e.target.value)}
                  data-testid={`input-plan-price-${idx}`}
                  className="text-lg font-mono text-emerald-500 font-semibold" />
              </div>

              {/* CUSTO DE IMPLANTAÇÃO */}
              <div className="rounded-lg border border-border bg-background p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">
                    <Wrench className="w-3 h-3" /> Custo de implantação (one-time)
                  </div>
                  <Badge variant="outline" className="text-amber-500 border-amber-500/40 text-[10px] tabular-nums" data-testid={`plan-total-implantacao-${idx}`}>
                    Total: {fmtBRL(impl)}
                  </Badge>
                </div>
                <div className="grid grid-cols-[minmax(120px,1fr)_90px_70px_100px] gap-2 text-[9px] uppercase tracking-wider text-muted-foreground px-1">
                  <div>Item</div><div className="text-right">Unitário</div><div className="text-right">Qtde</div><div className="text-right">Total</div>
                </div>
                {IMPLANTACAO_FIELDS.map((f) => (
                  <div key={f.key} className="grid grid-cols-[minmax(120px,1fr)_90px_70px_100px] gap-2 items-center">
                    <Label className="text-[11px] truncate">{f.label}</Label>
                    <Input type="number" step="0.01" min="0"
                      value={p[`custo_${f.key}_unit`] ?? 0}
                      onChange={(e) => setPlanField(idx, `custo_${f.key}_unit`, e.target.value)}
                      data-testid={`input-plan-${f.key}-unit-${idx}`}
                      title={f.unitLabel} className="text-xs font-mono h-8 text-right" />
                    <Input type="number" step="0.01" min="0"
                      value={p[`custo_${f.key}_qty`] ?? 0}
                      onChange={(e) => setPlanField(idx, `custo_${f.key}_qty`, e.target.value)}
                      data-testid={`input-plan-${f.key}-qty-${idx}`}
                      title={f.qtyLabel} className="text-xs font-mono h-8 text-right" />
                    <div className="text-xs font-mono text-right tabular-nums text-amber-500" data-testid={`plan-${f.key}-total-${idx}`}>
                      {fmtBRL(totalOf(p, f.key))}
                    </div>
                  </div>
                ))}
              </div>

              {/* CUSTO MENSAL FIXO */}
              <div className="rounded-lg border border-border bg-background p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">
                    <Zap className="w-3 h-3" /> Custo mensal fixo (recorrente)
                  </div>
                  <Badge variant="outline" className="text-amber-500 border-amber-500/40 text-[10px] tabular-nums" data-testid={`plan-total-mensal-${idx}`}>
                    Total: {fmtBRL(mens)}
                  </Badge>
                </div>
                <div className="grid grid-cols-[minmax(120px,1fr)_90px_70px_100px] gap-2 text-[9px] uppercase tracking-wider text-muted-foreground px-1">
                  <div>Item</div><div className="text-right">Unitário</div><div className="text-right">Qtde</div><div className="text-right">Total</div>
                </div>
                {MENSAL_UNIT_FIELDS.map((f) => (
                  <div key={f.key} className="grid grid-cols-[minmax(120px,1fr)_90px_70px_100px] gap-2 items-center">
                    <Label className="text-[11px] truncate">{f.label}</Label>
                    <Input type="number" step="0.01" min="0"
                      value={p[`custo_${f.key}_unit`] ?? 0}
                      onChange={(e) => setPlanField(idx, `custo_${f.key}_unit`, e.target.value)}
                      data-testid={`input-plan-${f.key}-unit-${idx}`}
                      title={f.unitLabel} className="text-xs font-mono h-8 text-right" />
                    <Input type="number" step="0.01" min="0"
                      value={p[`custo_${f.key}_qty`] ?? 0}
                      onChange={(e) => setPlanField(idx, `custo_${f.key}_qty`, e.target.value)}
                      data-testid={`input-plan-${f.key}-qty-${idx}`}
                      title={f.qtyLabel} className="text-xs font-mono h-8 text-right" />
                    <div className="text-xs font-mono text-right tabular-nums text-amber-500" data-testid={`plan-${f.key}-total-${idx}`}>
                      {fmtBRL(totalOf(p, f.key))}
                    </div>
                  </div>
                ))}
                {/* Imposto — % sobre preço */}
                <div className="grid grid-cols-[minmax(120px,1fr)_90px_70px_100px] gap-2 items-center">
                  <Label className="text-[11px] truncate flex items-center gap-1">
                    <ReceiptText className="w-3 h-3 text-muted-foreground" /> Imposto (% sobre venda)
                  </Label>
                  <Input type="number" step="0.1" min="0" max="100"
                    value={p.custo_imposto_pct ?? 0}
                    onChange={(e) => setPlanField(idx, "custo_imposto_pct", e.target.value)}
                    data-testid={`input-plan-imposto-pct-${idx}`}
                    title="% aplicado sobre o valor de venda mensal"
                    className="text-xs font-mono h-8 text-right" />
                  <div className="text-xs font-mono text-right tabular-nums text-muted-foreground self-center">
                    {num(p.custo_imposto_pct).toFixed(1)}%
                  </div>
                  <div className="text-xs font-mono text-right tabular-nums text-amber-500" data-testid={`plan-imposto-total-${idx}`}>
                    {fmtBRL(impostoValor(p))}
                  </div>
                </div>
              </div>

              {/* RESUMO: Duas visões — Payback + Lucro 12m + Recorrente */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="rounded-lg border border-primary/30 bg-primary/5 p-3" data-testid={`plan-visao-recorrente-${idx}`}>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Visão recorrente (só mensal)</div>
                  <div className={`text-2xl font-heading font-extrabold tabular-nums ${lm >= 0 ? "text-primary" : "text-red-500"}`} data-testid={`plan-lucro-mensal-${idx}`}>
                    {fmtBRL(lm)}<span className="text-xs text-muted-foreground font-normal">/mês</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className={mrgTone} data-testid={`plan-margem-${idx}`}>
                      Margem {fmtPct(marg)}
                    </Badge>
                    <Progress value={Math.min(Math.max(marg, 0), 100)} className="h-1.5 flex-1" />
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1">Preço − custo mensal (sem implantação)</div>
                </div>

                <div className="rounded-lg border-2 border-emerald-500/30 bg-emerald-500/5 p-3" data-testid={`plan-visao-12m-${idx}`}>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Lucro em 12 meses (com implantação)</div>
                  <div className={`text-2xl font-heading font-extrabold tabular-nums ${l12 >= 0 ? "text-emerald-500" : "text-red-500"}`} data-testid={`plan-lucro-12m-${idx}`}>
                    {fmtBRL(l12)}
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1 flex items-center justify-between">
                    <span>Receita 12m − implantação − 12×mensal</span>
                    <span className="tabular-nums font-mono">
                      Payback: {pb === null ? <span className="text-red-500">sem retorno</span> : pb === 0 ? "imediato" : `${pb.toFixed(1)} mês${pb >= 2 ? "es" : ""}`}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
